import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Target, AlertCircle, ChevronRight } from 'lucide-react';
import { Button } from '../components/Button';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { fetchLadderPathByStepId, createLadderWithSteps, deleteLadder } from '../lib/ladderUtils';
import type { Goal, LadderWithSteps, LadderStep } from '../types/ladder';

export function CreateNestedLadder() {
  const navigate = useNavigate();
  const { parentStepId } = useParams<{ parentStepId: string }>();
  const [goal, setGoal] = useState<Goal | null>(null);
  const [ladderPath, setLadderPath] = useState<LadderWithSteps[]>([]);
  const [parentStep, setParentStep] = useState<LadderStep | null>(null);
  const [steps, setSteps] = useState(['', '', '', '', '']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [createdLadderId, setCreatedLadderId] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!parentStepId) {
        navigate('/dash-growth-loop');
        return;
      }

      const data = await fetchLadderPathByStepId(parentStepId);
      if (!data) {
        navigate('/dash-growth-loop');
        return;
      }

      setGoal(data.goal);
      setLadderPath(data.ladderPath);
      setParentStep(data.parentStep);
      setIsLoading(false);
    };

    loadData();
  }, [parentStepId, navigate]);

  const handleStepChange = (index: number, value: string) => {
    const newSteps = [...steps];
    newSteps[index] = value;
    setSteps(newSteps);
  };

  const filledSteps = steps.filter((s) => s.trim() !== '');
  const allStepsFilled = filledSteps.length === 5;

  const currentLevel = ladderPath.length > 0 ? ladderPath[ladderPath.length - 1].level_number + 1 : 2;

  const handleCreateLadder = async () => {
    setError('');

    if (filledSteps.length === 0) {
      setError('Please enter at least one step');
      return;
    }

    if (!goal || !parentStep) return;

    setIsSubmitting(true);

    try {
      const parentLadder = ladderPath[ladderPath.length - 1];
      const result = await createLadderWithSteps(
        goal.id,
        parentStep.description,
        currentLevel,
        parentStep.id,
        filledSteps.map((s) => s.trim())
      );

      if (result) {
        setCreatedLadderId(result.ladder.id);
        navigate('/dash-growth-loop');
      } else {
        setError('Failed to create ladder. Please try again.');
      }
    } catch (err) {
      console.error('Error creating ladder:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAndCreateNested = async () => {
    setError('');

    if (filledSteps.length !== 5) {
      setError('All 5 steps must be completed to create a nested ladder');
      return;
    }

    if (!goal || !parentStep) return;

    setIsSubmitting(true);

    try {
      const result = await createLadderWithSteps(
        goal.id,
        parentStep.description,
        currentLevel,
        parentStep.id,
        filledSteps.map((s) => s.trim())
      );

      if (result) {
        setCreatedLadderId(result.ladder.id);
        const firstStep = result.steps.find((s) => s.step_number === 1);

        if (firstStep) {
          navigate(`/create-nested-ladder/${firstStep.id}`);
        } else {
          navigate('/dash-growth-loop');
        }
      } else {
        setError('Failed to create ladder. Please try again.');
      }
    } catch (err) {
      console.error('Error creating ladder:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (createdLadderId) {
      await deleteLadder(createdLadderId);
    }
    navigate('/dash-growth-loop');
  };

  if (isLoading) {
    return (
      <ProtectedLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner />
            <p className="text-gray-600 mt-4">Loading...</p>
          </div>
        </div>
      </ProtectedLayout>
    );
  }

  if (!goal || !parentStep || ladderPath.length === 0) {
    return null;
  }

  return (
    <ProtectedLayout>
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-gray-900">
                Create Level {currentLevel} Ladder
              </h2>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="mb-4">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <button
                    onClick={() => navigate('/dash-growth-loop')}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    {goal.name}
                  </button>
                  {ladderPath.map((ladder, index) => (
                    <div key={ladder.id} className="flex items-center gap-2">
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">
                        Level {ladder.level_number}
                      </span>
                    </div>
                  ))}
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900 font-medium">Level {currentLevel}</span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Target className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Breaking down: {parentStep.description}
                  </h3>
                  <p className="text-sm text-gray-600">
                    This is Step {parentStep.step_number} from Level {currentLevel - 1}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Action Steps (from completion backward)
                  </label>
                  <span className="text-sm text-gray-500">
                    {filledSteps.length}/5 steps completed
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  List the conditions that must be true for this step to be achieved. Start with the step that happens immediately before completion, then work backward until you reach today.
                </p>
                <div className="space-y-3">
                  <div>
                    <label htmlFor="step-5" className="block text-sm font-medium text-gray-700 mb-1">
                      Step 5
                    </label>
                    <input
                      type="text"
                      id="step-5"
                      value={steps[0]}
                      onChange={(e) => handleStepChange(0, e.target.value)}
                      placeholder="What must be true just before this outcome is fully achieved?"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="step-4" className="block text-sm font-medium text-gray-700 mb-1">
                      Step 4
                    </label>
                    <input
                      type="text"
                      id="step-4"
                      value={steps[1]}
                      onChange={(e) => handleStepChange(1, e.target.value)}
                      placeholder="Condition before Step 5"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="step-3" className="block text-sm font-medium text-gray-700 mb-1">
                      Step 3
                    </label>
                    <input
                      type="text"
                      id="step-3"
                      value={steps[2]}
                      onChange={(e) => handleStepChange(2, e.target.value)}
                      placeholder="Condition before Step 4"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="step-2" className="block text-sm font-medium text-gray-700 mb-1">
                      Step 2
                    </label>
                    <input
                      type="text"
                      id="step-2"
                      value={steps[3]}
                      onChange={(e) => handleStepChange(3, e.target.value)}
                      placeholder="Condition before Step 3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="step-1" className="block text-sm font-medium text-gray-700 mb-1">
                      Step 1
                    </label>
                    <input
                      type="text"
                      id="step-1"
                      value={steps[4]}
                      onChange={(e) => handleStepChange(4, e.target.value)}
                      placeholder="Condition before Step 2"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="flex flex-col gap-3">
                <Button
                  onClick={handleCreateLadder}
                  disabled={isSubmitting || filledSteps.length === 0}
                >
                  {isSubmitting ? 'Creating...' : 'Create Ladder'}
                </Button>

                <div className="relative">
                  <Button
                    onClick={handleSaveAndCreateNested}
                    disabled={isSubmitting || !allStepsFilled}
                    variant="secondary"
                    className="w-full"
                  >
                    {isSubmitting ? 'Creating...' : 'Save and Create Nested Ladder'}
                  </Button>
                  {!allStepsFilled && (
                    <div className="mt-2 flex items-start gap-2 text-sm text-gray-600">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <p>Complete all 5 steps to create a nested ladder</p>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleCancel}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}
